import React, { useEffect } from 'react';

import styles from './index.module.css';

const CloseIcon = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 256 256'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z' />
  </svg>
);

const PlayIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 256 256'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M232.4,114.49,88.32,26.35a16,16,0,0,0-16.2-.3A15.86,15.86,0,0,0,64,39.87V216.13A15.94,15.94,0,0,0,80,232a16.07,16.07,0,0,0,8.36-2.35L232.4,141.51a15.81,15.81,0,0,0,0-27 Z' />
  </svg>
);

const BULLETS = [
  "Mentees can request a call when they're ready to talk.",
  'Accept a call upon request or schedule a time that works for you.',
  'Your personal phone number is never shared.',
  'Regular phone calls with no app or special equipment required.',
  'Most conversations last about 15-30 minutes.',
];

// One-time welcome modal shown to mentors the first time they land in the app
// after the Phone Calls feature is enabled for their account. It mirrors the
// patient-facing voice-calls intro but uses mentor-specific copy and the
// mentor walkthrough video. There is intentionally no "Don't show this again"
// control: the modal is gated server-side by a seen_callouts key, so it only
// ever appears once.
export default function VoiceCallsWelcomeModal({ videoUrl, onDismiss, onSeeHow }) {
  useEffect(() => {
    const handler = (event) => {
      if (event.key === 'Escape') onDismiss?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onDismiss]);

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) onDismiss?.();
  };

  return (
    <div
      className={styles.backdrop}
      role='dialog'
      aria-modal='true'
      aria-labelledby='voice-calls-welcome-title'
      onClick={handleBackdropClick}
    >
      <div className={styles.modal}>
        <button type='button' className={styles.close} onClick={() => onDismiss?.()} aria-label='Close welcome'>
          <CloseIcon />
        </button>

        <div className={styles.hero}>
          <span className={`${styles.glow} ${styles.glow_top}`} aria-hidden='true' />
          <span className={`${styles.glow} ${styles.glow_bottom}`} aria-hidden='true' />
          <span className={styles.hero_shade} aria-hidden='true' />

          <p className={styles.hero_eyebrow}>A fresh start</p>
          <h2 id='voice-calls-welcome-title' className={styles.hero_title}>
            Welcome to the new Patient Partner
          </h2>
          <p className={styles.hero_sub}>We've refreshed the platform to make staying connected easier than ever.</p>
          <p className={styles.hero_sub}>
            Explore the updated experience and discover Voice Calls, a new way to connect with the people you mentor.
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.scroll}>
            <p className={styles.eyebrow}>Now available</p>
            <h3 className={styles.feature_title}>Voice calls</h3>
            <p className={styles.feature_sub}>
              Talk by phone with the people you mentor - on your schedule and only when you choose.
            </p>

            <div className={styles.video_card}>
              <span className={styles.video_glow} aria-hidden='true' />
              {videoUrl ? (
                <video className={styles.video} src={videoUrl} controls playsInline preload='metadata' />
              ) : (
                <span className={styles.video_play} aria-hidden='true'>
                  <PlayIcon />
                </span>
              )}
              <span className={styles.video_badge}>90-second tour</span>
            </div>

            <ul className={styles.bullets}>
              {BULLETS.map((bullet) => (
                <li key={bullet} className={styles.bullet}>
                  <span className={styles.bullet_dot} aria-hidden='true' />
                  <span className={styles.bullet_text}>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.footer}>
            <div className={styles.actions}>
              <button type='button' className={styles.primary_btn} onClick={() => onSeeHow?.()}>
                Learn How Calls Work
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
